import type {
  CalcType,
  CalcValue,
  FieldValueResolver,
  FormulaAnalysis,
  FormulaEvaluation,
  FormulaEvaluationContext,
  FormulaFieldMeta,
  FormulaIssue,
} from './types'
import { isFormulaDateValue, validateFormulaZone } from './dates'
import {
  FORMULA_FUNCTION_DEFS,
  FormulaRuntimeError,
  toBool,
  toDate,
  toNumber,
  toText,
} from './functions'

/**
 * Formula expression engine: tokenizer → Pratt parser → type analysis →
 * evaluator. Independent of application state and the host timezone so it can run
 * on both the API and the web client.
 *
 * Grammar (highest precedence last):
 *   or:         and (OR|`||` and)*
 *   and:        comparison (AND|`&&` comparison)*
 *   comparison: concat ((=|==|!=|<>|<|<=|>|>=) concat)?
 *   concat:     additive (`&` additive)*
 *   additive:   multiplicative ((+|-) multiplicative)*
 *   multiplicative: power ((*|/|%) power)*
 *   power:      unary (^ power)?          — right associative
 *   unary:      (-|NOT|!) unary | primary
 *   primary:    number | string | TRUE | FALSE | NULL | {field} |
 *               IDENT(args) | ( or )
 */

// ============ Tokenizer ============

type TokenType =
  | 'number'
  | 'string'
  | 'field'
  | 'ident'
  | 'op'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'eof'

interface Token {
  type: TokenType
  value: string
  start: number
  end: number
}

export class FormulaParseError extends Error {
  constructor(
    message: string,
    public start?: number,
    public end?: number,
  ) {
    super(message)
  }
}

const OPERATOR_STARTS = new Set([
  '+',
  '-',
  '*',
  '/',
  '%',
  '^',
  '&',
  '=',
  '!',
  '<',
  '>',
  '|',
])

function tokenize(expression: string): Token[] {
  const tokens: Token[] = []
  let pos = 0

  while (pos < expression.length) {
    const char = expression[pos]

    if (/\s/.test(char)) {
      pos++
      continue
    }

    // Field reference: {field_name}
    if (char === '{') {
      const close = expression.indexOf('}', pos)
      if (close === -1) {
        throw new FormulaParseError(
          'Unclosed field reference — missing "}"',
          pos,
          expression.length,
        )
      }
      const name = expression.slice(pos + 1, close).trim()
      if (!name) {
        throw new FormulaParseError(
          'Empty field reference "{}"',
          pos,
          close + 1,
        )
      }
      tokens.push({ type: 'field', value: name, start: pos, end: close + 1 })
      pos = close + 1
      continue
    }

    // String literal: "..." or '...'
    if (char === '"' || char === "'") {
      let text = ''
      let cursor = pos + 1
      let closed = false
      while (cursor < expression.length) {
        const current = expression[cursor]
        if (current === '\\' && cursor + 1 < expression.length) {
          text += expression[cursor + 1]
          cursor += 2
          continue
        }
        if (current === char) {
          closed = true
          break
        }
        text += current
        cursor++
      }
      if (!closed) {
        throw new FormulaParseError(
          'Unclosed text — missing closing quote',
          pos,
          expression.length,
        )
      }
      tokens.push({ type: 'string', value: text, start: pos, end: cursor + 1 })
      pos = cursor + 1
      continue
    }

    // Number literal
    if (
      /[0-9]/.test(char) ||
      (char === '.' && /[0-9]/.test(expression[pos + 1] ?? ''))
    ) {
      const match = /^[0-9]*\.?[0-9]+/.exec(expression.slice(pos))
      if (!match) {
        throw new FormulaParseError('Invalid number', pos, pos + 1)
      }
      tokens.push({
        type: 'number',
        value: match[0],
        start: pos,
        end: pos + match[0].length,
      })
      pos += match[0].length
      continue
    }

    // Identifier / keyword (function names, TRUE/FALSE/NULL/AND/OR/NOT)
    if (/[A-Za-z_]/.test(char)) {
      const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(expression.slice(pos))!
      tokens.push({
        type: 'ident',
        value: match[0],
        start: pos,
        end: pos + match[0].length,
      })
      pos += match[0].length
      continue
    }

    if (char === '(') {
      tokens.push({ type: 'lparen', value: '(', start: pos, end: pos + 1 })
      pos++
      continue
    }
    if (char === ')') {
      tokens.push({ type: 'rparen', value: ')', start: pos, end: pos + 1 })
      pos++
      continue
    }
    if (char === ',') {
      tokens.push({ type: 'comma', value: ',', start: pos, end: pos + 1 })
      pos++
      continue
    }

    if (OPERATOR_STARTS.has(char)) {
      const two = expression.slice(pos, pos + 2)
      if (['==', '!=', '<>', '<=', '>=', '&&', '||'].includes(two)) {
        tokens.push({ type: 'op', value: two, start: pos, end: pos + 2 })
        pos += 2
      } else {
        tokens.push({ type: 'op', value: char, start: pos, end: pos + 1 })
        pos++
      }
      continue
    }

    throw new FormulaParseError(`Unexpected character "${char}"`, pos, pos + 1)
  }

  tokens.push({
    type: 'eof',
    value: '',
    start: expression.length,
    end: expression.length,
  })
  return tokens
}

// ============ Parser ============

export type AstNode =
  | { kind: 'number'; value: number; start: number; end: number }
  | { kind: 'string'; value: string; start: number; end: number }
  | { kind: 'boolean'; value: boolean; start: number; end: number }
  | { kind: 'null'; start: number; end: number }
  | { kind: 'field'; name: string; start: number; end: number }
  | {
      kind: 'unary'
      op: '-' | 'not'
      operand: AstNode
      start: number
      end: number
    }
  | {
      kind: 'binary'
      op: string
      left: AstNode
      right: AstNode
      start: number
      end: number
    }
  | { kind: 'call'; name: string; args: AstNode[]; start: number; end: number }

class Parser {
  private index = 0

  constructor(private tokens: Token[]) {}

  parse(): AstNode {
    const node = this.parseOr()
    const token = this.peek()
    if (token.type !== 'eof') {
      throw new FormulaParseError(
        `Unexpected "${token.value}"`,
        token.start,
        token.end,
      )
    }
    return node
  }

  private peek(): Token {
    return this.tokens[this.index]
  }

  private next(): Token {
    return this.tokens[this.index++]
  }

  private isKeyword(token: Token, word: string): boolean {
    return token.type === 'ident' && token.value.toUpperCase() === word
  }

  private parseOr(): AstNode {
    let left = this.parseAnd()
    while (true) {
      const token = this.peek()
      if (
        (token.type === 'op' && token.value === '||') ||
        this.isKeyword(token, 'OR')
      ) {
        this.next()
        const right = this.parseAnd()
        left = {
          kind: 'binary',
          op: 'or',
          left,
          right,
          start: left.start,
          end: right.end,
        }
      } else {
        return left
      }
    }
  }

  private parseAnd(): AstNode {
    let left = this.parseComparison()
    while (true) {
      const token = this.peek()
      if (
        (token.type === 'op' && token.value === '&&') ||
        this.isKeyword(token, 'AND')
      ) {
        this.next()
        const right = this.parseComparison()
        left = {
          kind: 'binary',
          op: 'and',
          left,
          right,
          start: left.start,
          end: right.end,
        }
      } else {
        return left
      }
    }
  }

  private parseComparison(): AstNode {
    const left = this.parseConcat()
    const token = this.peek()
    if (
      token.type === 'op' &&
      ['=', '==', '!=', '<>', '<', '<=', '>', '>='].includes(token.value)
    ) {
      this.next()
      const right = this.parseConcat()
      const op =
        token.value === '==' ? '=' : token.value === '<>' ? '!=' : token.value
      return {
        kind: 'binary',
        op,
        left,
        right,
        start: left.start,
        end: right.end,
      }
    }
    return left
  }

  private parseConcat(): AstNode {
    let left = this.parseAdditive()
    while (this.peek().type === 'op' && this.peek().value === '&') {
      this.next()
      const right = this.parseAdditive()
      left = {
        kind: 'binary',
        op: '&',
        left,
        right,
        start: left.start,
        end: right.end,
      }
    }
    return left
  }

  private parseAdditive(): AstNode {
    let left = this.parseMultiplicative()
    while (
      this.peek().type === 'op' &&
      (this.peek().value === '+' || this.peek().value === '-')
    ) {
      const op = this.next().value
      const right = this.parseMultiplicative()
      left = {
        kind: 'binary',
        op,
        left,
        right,
        start: left.start,
        end: right.end,
      }
    }
    return left
  }

  private parseMultiplicative(): AstNode {
    let left = this.parsePower()
    while (
      this.peek().type === 'op' &&
      ['*', '/', '%'].includes(this.peek().value)
    ) {
      const op = this.next().value
      const right = this.parsePower()
      left = {
        kind: 'binary',
        op,
        left,
        right,
        start: left.start,
        end: right.end,
      }
    }
    return left
  }

  private parsePower(): AstNode {
    const left = this.parseUnary()
    if (this.peek().type === 'op' && this.peek().value === '^') {
      this.next()
      const right = this.parsePower() // right associative
      return {
        kind: 'binary',
        op: '^',
        left,
        right,
        start: left.start,
        end: right.end,
      }
    }
    return left
  }

  private parseUnary(): AstNode {
    const token = this.peek()
    if (token.type === 'op' && token.value === '-') {
      this.next()
      const operand = this.parseUnary()
      return {
        kind: 'unary',
        op: '-',
        operand,
        start: token.start,
        end: operand.end,
      }
    }
    if (
      (token.type === 'op' && token.value === '!') ||
      this.isKeyword(token, 'NOT')
    ) {
      // NOT(x) is also a registered function; only treat NOT as a unary
      // keyword when it is not immediately followed by "(".
      const isFunctionCall =
        token.type === 'ident' && this.tokens[this.index + 1]?.type === 'lparen'
      if (!isFunctionCall) {
        this.next()
        const operand = this.parseUnary()
        return {
          kind: 'unary',
          op: 'not',
          operand,
          start: token.start,
          end: operand.end,
        }
      }
    }
    return this.parsePrimary()
  }

  private parsePrimary(): AstNode {
    const token = this.next()

    if (token.type === 'number') {
      return {
        kind: 'number',
        value: Number(token.value),
        start: token.start,
        end: token.end,
      }
    }
    if (token.type === 'string') {
      return {
        kind: 'string',
        value: token.value,
        start: token.start,
        end: token.end,
      }
    }
    if (token.type === 'field') {
      return {
        kind: 'field',
        name: token.value,
        start: token.start,
        end: token.end,
      }
    }
    if (token.type === 'lparen') {
      const node = this.parseOr()
      const close = this.next()
      if (close.type !== 'rparen') {
        throw new FormulaParseError(
          'Missing closing ")"',
          close.start,
          close.end,
        )
      }
      return node
    }
    if (token.type === 'ident') {
      const upper = token.value.toUpperCase()
      if (upper === 'TRUE' || upper === 'FALSE') {
        return {
          kind: 'boolean',
          value: upper === 'TRUE',
          start: token.start,
          end: token.end,
        }
      }
      if (upper === 'NULL' || upper === 'BLANK') {
        return { kind: 'null', start: token.start, end: token.end }
      }
      if (this.peek().type === 'lparen') {
        this.next() // consume (
        const args: AstNode[] = []
        if (this.peek().type !== 'rparen') {
          args.push(this.parseOr())
          while (this.peek().type === 'comma') {
            this.next()
            args.push(this.parseOr())
          }
        }
        const close = this.next()
        if (close.type !== 'rparen') {
          throw new FormulaParseError(
            'Missing closing ")" for function call',
            close.start,
            close.end,
          )
        }
        return {
          kind: 'call',
          name: upper,
          args,
          start: token.start,
          end: close.end,
        }
      }
      throw new FormulaParseError(
        `Unknown name "${token.value}" — field references use {curly braces}`,
        token.start,
        token.end,
      )
    }

    throw new FormulaParseError(
      token.type === 'eof'
        ? 'Formula ends unexpectedly'
        : `Unexpected "${token.value}"`,
      token.start,
      token.end,
    )
  }
}

// Parsed-expression cache (bounded; expressions are tiny)
const parseCache = new Map<string, AstNode>()
const PARSE_CACHE_LIMIT = 500

export function parseFormula(expression: string): AstNode {
  const cached = parseCache.get(expression)
  if (cached) return cached
  const ast = new Parser(tokenize(expression)).parse()
  if (parseCache.size >= PARSE_CACHE_LIMIT) {
    const oldest = parseCache.keys().next().value
    if (oldest !== undefined) parseCache.delete(oldest)
  }
  parseCache.set(expression, ast)
  return ast
}

// ============ Analysis (type check + dependencies) ============

const NUMERIC_OK: ReadonlySet<CalcType> = new Set(['NUMBER', 'NULL', 'ANY'])

function checkNumeric(
  type: CalcType,
  node: AstNode,
  role: string,
  issues: FormulaIssue[],
): void {
  if (!NUMERIC_OK.has(type)) {
    issues.push({
      message: `${role} must be a number, but got ${type.toLowerCase()}${
        type === 'STRING' ? ' — use & or CONCAT to join text' : ''
      }`,
      start: node.start,
      end: node.end,
    })
  }
}

export function analyzeFormula(
  expression: string,
  fields: FormulaFieldMeta[],
): FormulaAnalysis {
  const issues: FormulaIssue[] = []
  const dependencies = new Set<string>()

  let ast: AstNode
  try {
    ast = parseFormula(expression)
  } catch (error) {
    const parseError = error as FormulaParseError
    return {
      ok: false,
      resultType: null,
      dependencies: [],
      issues: [
        {
          message: parseError.message,
          start: parseError.start,
          end: parseError.end,
        },
      ],
    }
  }

  const fieldsByName = new Map<string, FormulaFieldMeta>()
  for (const field of fields) {
    fieldsByName.set(field.name.toLowerCase(), field)
  }

  const inferType = (node: AstNode): CalcType => {
    switch (node.kind) {
      case 'number':
        return 'NUMBER'
      case 'string':
        return 'STRING'
      case 'boolean':
        return 'BOOLEAN'
      case 'null':
        return 'NULL'
      case 'field': {
        const meta = fieldsByName.get(node.name.toLowerCase())
        if (!meta) {
          issues.push({
            message: `Unknown field "${node.name}"`,
            start: node.start,
            end: node.end,
          })
          return 'ANY'
        }
        if (meta.isFormula) {
          issues.push({
            message: `"${meta.label}" is a calculated field — formulas cannot reference other calculated fields yet`,
            start: node.start,
            end: node.end,
          })
          return 'ANY'
        }
        dependencies.add(meta.name)
        return meta.type
      }
      case 'unary': {
        const operandType = inferType(node.operand)
        if (node.op === '-') {
          checkNumeric(operandType, node.operand, 'This value', issues)
          return 'NUMBER'
        }
        return 'BOOLEAN'
      }
      case 'binary': {
        const leftType = inferType(node.left)
        const rightType = inferType(node.right)
        switch (node.op) {
          case '+':
          case '-':
          case '*':
          case '/':
          case '%':
          case '^':
            checkNumeric(leftType, node.left, 'The left side', issues)
            checkNumeric(rightType, node.right, 'The right side', issues)
            return 'NUMBER'
          case '&':
            return 'STRING'
          case 'and':
          case 'or':
            return 'BOOLEAN'
          default:
            // comparisons
            return 'BOOLEAN'
        }
      }
      case 'call': {
        const def = FORMULA_FUNCTION_DEFS.get(node.name)
        if (!def) {
          issues.push({
            message: `Unknown function "${node.name}"`,
            start: node.start,
            end: node.end,
          })
          node.args.forEach(inferType)
          return 'ANY'
        }
        if (node.args.length < def.minArgs || node.args.length > def.maxArgs) {
          const expected =
            def.maxArgs === Infinity
              ? `at least ${def.minArgs}`
              : def.minArgs === def.maxArgs
                ? `${def.minArgs}`
                : `${def.minArgs}-${def.maxArgs}`
          issues.push({
            message: `${def.doc.signature} expects ${expected} argument${
              def.minArgs === 1 && def.maxArgs === 1 ? '' : 's'
            }`,
            start: node.start,
            end: node.end,
          })
        }
        const argTypes = node.args.map(inferType)
        argTypes.forEach((argType, argIndex) => {
          const paramType =
            def.params[Math.min(argIndex, def.params.length - 1)] ?? 'ANY'
          if (paramType === 'NUMBER') {
            checkNumeric(argType, node.args[argIndex], 'This argument', issues)
          }
          if (
            paramType === 'DATE' &&
            !['DATE', 'STRING', 'NULL', 'ANY'].includes(argType)
          ) {
            issues.push({
              message: 'This argument must be a date',
              start: node.args[argIndex].start,
              end: node.args[argIndex].end,
            })
          }
        })
        return typeof def.returns === 'function'
          ? def.returns(argTypes)
          : def.returns
      }
    }
  }

  const resultType = inferType(ast)
  return {
    ok: issues.length === 0,
    resultType: issues.length === 0 ? resultType : null,
    dependencies: [...dependencies],
    issues,
  }
}

/** Field names referenced by an expression (empty on parse errors) */
export function extractFormulaDependencies(expression: string): string[] {
  try {
    const ast = parseFormula(expression)
    const deps = new Set<string>()
    const walk = (node: AstNode): void => {
      switch (node.kind) {
        case 'field':
          deps.add(node.name)
          break
        case 'unary':
          walk(node.operand)
          break
        case 'binary':
          walk(node.left)
          walk(node.right)
          break
        case 'call':
          node.args.forEach(walk)
          break
        default:
          break
      }
    }
    walk(ast)
    return [...deps]
  } catch {
    return []
  }
}

// ============ Evaluation ============

function normalizeRawValue(raw: unknown): CalcValue {
  if (raw === null || raw === undefined) return null
  if (
    typeof raw === 'number' ||
    typeof raw === 'string' ||
    typeof raw === 'boolean' ||
    raw instanceof Date
  ) {
    return raw
  }
  // Dropdown-style objects: { value, label } — use the value
  if (typeof raw === 'object' && 'value' in (raw as Record<string, unknown>)) {
    return normalizeRawValue((raw as Record<string, unknown>).value)
  }
  // Reference-style objects: { id, name } — use the display name
  if (typeof raw === 'object' && 'name' in (raw as Record<string, unknown>)) {
    return normalizeRawValue((raw as Record<string, unknown>).name)
  }
  return null
}

function compare(
  op: string,
  left: CalcValue,
  right: CalcValue,
  context: FormulaEvaluationContext,
): boolean {
  if (op === '=' || op === '!=') {
    const equals = looseEquals(left, right, context)
    return op === '=' ? equals : !equals
  }
  // Ordered comparison: dates compare by time, otherwise numbers, otherwise text
  if (left === null || right === null) return false
  let leftOrd: number | string
  let rightOrd: number | string
  if (
    left instanceof Date ||
    right instanceof Date ||
    (isFormulaDateValue(left) && isFormulaDateValue(right))
  ) {
    const leftDate = toDate(left, context)
    const rightDate = toDate(right, context)
    if (leftDate === null || rightDate === null) return false
    leftOrd = leftDate.getTime()
    rightOrd = rightDate.getTime()
  } else if (typeof left === 'number' || typeof right === 'number') {
    const leftNum = toNumber(left)
    const rightNum = toNumber(right)
    if (leftNum === null || rightNum === null) return false
    leftOrd = leftNum
    rightOrd = rightNum
  } else {
    leftOrd = toText(left)
    rightOrd = toText(right)
  }
  switch (op) {
    case '<':
      return leftOrd < rightOrd
    case '<=':
      return leftOrd <= rightOrd
    case '>':
      return leftOrd > rightOrd
    case '>=':
      return leftOrd >= rightOrd
    default:
      return false
  }
}

function looseEquals(
  left: CalcValue,
  right: CalcValue,
  context: FormulaEvaluationContext,
): boolean {
  if (left === null && right === null) return true
  if (left === null || right === null) return false
  if (
    left instanceof Date ||
    right instanceof Date ||
    (isFormulaDateValue(left) && isFormulaDateValue(right))
  ) {
    try {
      return (
        toDate(left, context)?.getTime() === toDate(right, context)?.getTime()
      )
    } catch {
      return false
    }
  }
  if (typeof left === 'number' || typeof right === 'number') {
    try {
      return toNumber(left) === toNumber(right)
    } catch {
      return false
    }
  }
  if (typeof left === 'boolean' || typeof right === 'boolean') {
    return toBool(left) === toBool(right)
  }
  return toText(left) === toText(right)
}

function evaluateNode(
  node: AstNode,
  resolve: FieldValueResolver,
  context: FormulaEvaluationContext,
): CalcValue {
  switch (node.kind) {
    case 'number':
      return node.value
    case 'string':
      return node.value
    case 'boolean':
      return node.value
    case 'null':
      return null
    case 'field':
      return normalizeRawValue(resolve(node.name))
    case 'unary': {
      const operand = evaluateNode(node.operand, resolve, context)
      if (node.op === '-') {
        const num = toNumber(operand)
        return num === null ? null : -num
      }
      return !toBool(operand)
    }
    case 'binary': {
      if (node.op === 'and') {
        return (
          toBool(evaluateNode(node.left, resolve, context)) &&
          toBool(evaluateNode(node.right, resolve, context))
        )
      }
      if (node.op === 'or') {
        return (
          toBool(evaluateNode(node.left, resolve, context)) ||
          toBool(evaluateNode(node.right, resolve, context))
        )
      }
      const left = evaluateNode(node.left, resolve, context)
      const right = evaluateNode(node.right, resolve, context)
      switch (node.op) {
        case '&':
          return toText(left) + toText(right)
        case '=':
        case '!=':
        case '<':
        case '<=':
        case '>':
        case '>=':
          return compare(node.op, left, right, context)
        default: {
          const leftNum = toNumber(left)
          const rightNum = toNumber(right)
          if (leftNum === null || rightNum === null) return null
          switch (node.op) {
            case '+':
              return leftNum + rightNum
            case '-':
              return leftNum - rightNum
            case '*':
              return leftNum * rightNum
            case '/':
              return rightNum === 0 ? null : leftNum / rightNum
            case '%':
              return rightNum === 0 ? null : leftNum % rightNum
            case '^':
              return leftNum ** rightNum
            default:
              throw new FormulaRuntimeError(`Unknown operator "${node.op}"`)
          }
        }
      }
    }
    case 'call': {
      const def = FORMULA_FUNCTION_DEFS.get(node.name)
      if (!def) {
        throw new FormulaRuntimeError(`Unknown function "${node.name}"`)
      }
      const args = node.args.map(arg => evaluateNode(arg, resolve, context))
      return def.impl(args, context)
    }
  }
}

/**
 * Evaluate a formula against a record. Runtime failures return an error
 * message instead of throwing so table cells degrade to blank gracefully.
 * Omitted context uses UTC, never a machine-local timezone. Application callers
 * supply their business timezone; one captured clock serves the whole expression.
 */
export function evaluateFormula(
  expression: string,
  resolve: FieldValueResolver,
  options: FormulaEvaluationContext = { timeZone: 'UTC' },
): FormulaEvaluation {
  try {
    const context = {
      timeZone: options.timeZone,
      now: new Date(options.now?.getTime() ?? Date.now()),
    }
    if (!Number.isFinite(context.now.getTime()))
      throw new FormulaRuntimeError('Invalid formula clock')
    validateFormulaZone(context.timeZone)
    const ast = parseFormula(expression)
    return { value: evaluateNode(ast, resolve, context), error: null }
  } catch (error) {
    return {
      value: null,
      error: error instanceof Error ? error.message : 'Formula failed',
    }
  }
}
